import {MediaClip, ClipFadeConfig, ClipTransitionPoint} from "../media";
import {AudioClipProps} from "./audio.interfaces";
import {Track} from "../../tracks";
import {MediaSourceRef} from "../../sources/media-source";
import {IOError} from "../../errors";
import {AudioMixer, MixerTrackHandle} from "../../media/audio-mixer";
import {AudioBufferSink, Input, UrlSource, ALL_FORMATS} from "mediabunny";
import {Timestamp} from "../../models";

const HALF_PI = Math.PI / 2;

/**
 * Per-clip peak limiter state (persists across chunks for smooth envelope)
 */
type LimiterState = {
    envelope: number;  // Current envelope level (0-1)
};

/**
 * Apply fade envelope to audio samples in-place using equal-power curves.
 * Equal-power uses sin/cos curves which maintain constant perceived loudness.
 * 
 * Handles:
 * - Clip edge fades (fadeIn at start, fadeOut at end)
 * - Transition point fades (fadeOut before point, fadeIn after point)
 */
function applyFadeEnvelope(
    planes: Float32Array[],
    sampleRate: number,
    chunkStartSrcSec: number,
    chunkDurationSrcSec: number,
    clipStartSrcSec: number,
    clipEndSrcSec: number,
    playbackRate: number,
    fadeIn?: ClipFadeConfig,
    fadeOut?: ClipFadeConfig,
    transitionPoints?: ClipTransitionPoint[]
): void {
    const numFrames = planes[0]?.length ?? 0;
    if (numFrames === 0) return;
    
    if (!Number.isFinite(clipStartSrcSec) || !Number.isFinite(clipEndSrcSec)) return;
    if (clipEndSrcSec <= clipStartSrcSec) return;
    const rate = Number.isFinite(playbackRate) && playbackRate > 0 ? playbackRate : 1;

    // Fade durations are specified in timeline seconds; scale into source time.
    const fadeInDurationSrcSec = ((fadeIn?.durationMs ?? 0) / 1000) * rate;
    const fadeOutDurationSrcSec = ((fadeOut?.durationMs ?? 0) / 1000) * rate;

    const fadeInEndSrcSec = clipStartSrcSec + fadeInDurationSrcSec;
    const fadeOutStartSrcSec = clipEndSrcSec - fadeOutDurationSrcSec;
    
    const chunkEndSrcSec = chunkStartSrcSec + chunkDurationSrcSec;
    
    // Check if any fades are configured
    const hasEdgeFades = fadeInDurationSrcSec > 0 || fadeOutDurationSrcSec > 0;
    const hasTransitionFades = transitionPoints && transitionPoints.some(tp => 
        tp.transition?.fadeIn?.durationMs || tp.transition?.fadeOut?.durationMs
    );
    
    if (!hasEdgeFades && !hasTransitionFades) return;
    
    // Check if this chunk overlaps any fade region
    const overlapsEdgeIn =
        fadeInDurationSrcSec > 0 &&
        chunkStartSrcSec < fadeInEndSrcSec &&
        chunkEndSrcSec > clipStartSrcSec;
    const overlapsEdgeOut =
        fadeOutDurationSrcSec > 0 &&
        chunkStartSrcSec < clipEndSrcSec &&
        chunkEndSrcSec > fadeOutStartSrcSec;
    
    // Build list of active transitions for this chunk
    const activeTransitions: Array<{
        pointSec: number;
        fadeOutDurationSrcSec: number;
        fadeInDurationSrcSec: number;
    }> = [];
    
    if (transitionPoints) {
        for (const tp of transitionPoints) {
            const tpFadeOutDurSrcSec = ((tp.transition?.fadeOut?.durationMs ?? 0) / 1000) * rate;
            const tpFadeInDurSrcSec = ((tp.transition?.fadeIn?.durationMs ?? 0) / 1000) * rate;
            if (tpFadeOutDurSrcSec <= 0 && tpFadeInDurSrcSec <= 0) continue;
            
            const pointSec = tp.srcTimeSec;
            if (!Number.isFinite(pointSec)) continue;
            if (pointSec < clipStartSrcSec || pointSec > clipEndSrcSec) continue;

            const fadeOutStart = pointSec - tpFadeOutDurSrcSec;
            const fadeInEnd = pointSec + tpFadeInDurSrcSec;
            
            if (chunkEndSrcSec > fadeOutStart && chunkStartSrcSec < fadeInEnd) {
                activeTransitions.push({
                    pointSec,
                    fadeOutDurationSrcSec: tpFadeOutDurSrcSec,
                    fadeInDurationSrcSec: tpFadeInDurSrcSec,
                });
            }
        }
    }
    
    if (!overlapsEdgeIn && !overlapsEdgeOut && activeTransitions.length === 0) return;
    
    // Apply per-sample gain with equal-power curves
    for (let i = 0; i < numFrames; i++) {
        const sampleTimeSec = chunkStartSrcSec + (i / sampleRate);
        let gain = 1.0;
        
        // Clip edge fade-in
        if (fadeInDurationSrcSec > 0 && sampleTimeSec >= clipStartSrcSec && sampleTimeSec < fadeInEndSrcSec) {
            const t = (sampleTimeSec - clipStartSrcSec) / fadeInDurationSrcSec;
            gain *= Math.sin(t * HALF_PI);
        }
        
        // Clip edge fade-out
        if (fadeOutDurationSrcSec > 0 && sampleTimeSec >= fadeOutStartSrcSec && sampleTimeSec < clipEndSrcSec) {
            const t = (sampleTimeSec - fadeOutStartSrcSec) / fadeOutDurationSrcSec;
            gain *= Math.cos(t * HALF_PI);
        }
        
        // Transition point fades
        for (const tp of activeTransitions) {
            // Fade-out leading up to transition point
            if (tp.fadeOutDurationSrcSec > 0) {
                const fadeOutStart = tp.pointSec - tp.fadeOutDurationSrcSec;
                if (sampleTimeSec >= fadeOutStart && sampleTimeSec < tp.pointSec) {
                    const t = (sampleTimeSec - fadeOutStart) / tp.fadeOutDurationSrcSec;
                    gain *= Math.cos(t * HALF_PI);
                }
            }
            
            // Fade-in after transition point
            if (tp.fadeInDurationSrcSec > 0) {
                const fadeInEnd = tp.pointSec + tp.fadeInDurationSrcSec;
                if (sampleTimeSec >= tp.pointSec && sampleTimeSec < fadeInEnd) {
                    const t = (sampleTimeSec - tp.pointSec) / tp.fadeInDurationSrcSec;
                    gain *= Math.sin(t * HALF_PI);
                }
            }
        }
        
        // Apply gain to all channels
        for (const plane of planes) {
            plane[i] *= gain;
        }
    }
}

export class AudioClip extends MediaClip<AudioClipProps> {
    public readonly type = 'audio';
    public track?: Track<AudioClip>;

    public source: MediaSourceRef;

    private _num_channels?: number;
    private _sample_rate?: number;
    private _audioSink?: AudioBufferSink;
    private _audioIter?: AsyncGenerator<{ buffer: AudioBuffer; timestamp: number }, void>;
    private _audioPrimeSeq = 0;

    private _mixer?: AudioMixer;
    private _mixerTrack?: MixerTrackHandle;

    private _audioCtx?: AudioContext;
    private _audioCtxStartTime: number | null = null;

    // Studio Sound state
    private _studioSoundEnabled = false;
    private _originalSourceUrl?: string;
    private _denoisedSourceUrl?: string;
    private _limiterState: LimiterState = { envelope: 0 };

    public constructor(source?: MediaSourceRef, props: AudioClipProps = {}, id?: string) {
        super();
        this.source = source;
        Object.assign(this, props);
        if (props.gain != null) this.gain = props.gain;

        if (id) {
            this.id = id;
        }

        this.init().then(() => {
        });
    }

    public mixerTrack(): MixerTrackHandle | undefined {
        return this._mixerTrack;
    }

    public async init(): Promise<void> {
        if (this.source) {
            try {
                const input = this.source.input;
                const audioTrack = await input.getPrimaryAudioTrack();

                if (!audioTrack) {
                    this.state = 'ERROR';
                    throw new IOError({code: 'noVideoTrack', message: 'File has no video track.'});
                }
                if (audioTrack.codec === null || !(await audioTrack.canDecode())) {
                    this.state = 'ERROR';
                    throw new IOError({code: 'unsupportedVideoCodec', message: 'Unsupported/undecodable video codec.'});
                }

                this._audioSink = new AudioBufferSink(audioTrack);
                this._num_channels = audioTrack.numberOfChannels;
                this._sample_rate = audioTrack.sampleRate;
                this.duration.seconds = await audioTrack.computeDuration();
                this.state = 'READY';
            } catch (err) {
                this.state = 'ERROR';
                throw err;
            }
        }
    }

    public async primeAudio(compTimeSec: number, mixer: AudioMixer, prebufferSec = 0.2) {
        if (!this._audioSink || !this._mixerTrack) return;

        this._audioPrimeSeq += 1;
        const primeSeq = this._audioPrimeSeq;

        const clipLocal = this.CompositionTimeToClipTime(compTimeSec);

        // Reset limiter state on seek/new playback to avoid artifacts
        this._limiterState.envelope = 0;

        const MAX_BUFFER_AHEAD_SEC = 15;
        const BACKPRESSURE_SLEEP_MS = 25;
        const EPS = 1e-3;
        const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

        // Dispose any previous iterator
        await (this._audioIter as any)?.return?.();
        mixer.flushTrack(this._mixerTrack, clipLocal);

        const effectiveSampleRate = this._sample_rate || 48000;
        let acc = 0;

        const segsRaw: Array<{ srcStartSec: number; srcEndSec: number; compStartSec: number }> =
            Array.isArray((this as any).segments) ? ((this as any).segments as any) : [];
        const clipStartSrcSec =
            Array.isArray(segsRaw) && segsRaw.length > 0
                ? Number(segsRaw[0].srcStartSec ?? (this.range?.[0]?.seconds ?? 0))
                : Number(this.range?.[0]?.seconds ?? 0);
        const clipEndSrcSec =
            Array.isArray(segsRaw) && segsRaw.length > 0
                ? Number(segsRaw[segsRaw.length - 1].srcEndSec ?? (this.range?.[1]?.seconds ?? clipStartSrcSec))
                : Number(this.range?.[1]?.seconds ?? clipStartSrcSec);
        const playbackRate = Number(this.playbackRate ?? 1);

        // Get fade configurations.
        const fadeIn = this.fadeIn;
        const fadeOut = this.fadeOut;
        const transitionPts = this.transitionPoints;

        const segs: Array<{ srcStartSec: number; srcEndSec: number; compStartSec: number }> =
            Array.isArray(segsRaw) && segsRaw.length > 0
                ? segsRaw
                : [
                      {
                          srcStartSec: clipStartSrcSec,
                          srcEndSec: clipEndSrcSec,
                          compStartSec: this.start?.seconds ?? 0,
                      },
                  ];

        let segIdx = segs.findIndex((s) => clipLocal >= s.srcStartSec && clipLocal < s.srcEndSec);
        if (segIdx < 0) segIdx = segs.findIndex((s) => clipLocal < s.srcEndSec);
        if (segIdx < 0) return;

        let seg = segs[segIdx];

        // Track both source-time and the corresponding composition-time buffer frontier.
        let nextChunkTimeSec = clipLocal;
        let nextChunkCompTimeSec = seg.compStartSec + Math.max(0, nextChunkTimeSec - seg.srcStartSec);

        let iter: AsyncGenerator<{ buffer: AudioBuffer; timestamp: number }, void> = this._audioSink.buffers(
            nextChunkTimeSec,
            seg.srcEndSec
        );
        this._audioIter = iter;

        const advanceSegment = async (): Promise<boolean> => {
            if (this._audioPrimeSeq !== primeSeq) return false;
            segIdx += 1;
            if (segIdx >= segs.length) return false;
            seg = segs[segIdx];
            nextChunkTimeSec = seg.srcStartSec;
            nextChunkCompTimeSec = seg.compStartSec;
            try {
                await (iter as any)?.return?.();
            } catch {}
            iter = this._audioSink!.buffers(nextChunkTimeSec, seg.srcEndSec);
            this._audioIter = iter;
            return true;
        };

        const readNextBuffer = async (): Promise<AudioBuffer | null> => {
            while (true) {
                if (this._audioPrimeSeq !== primeSeq) return null;
                const n = await iter.next();
                if (this._audioPrimeSeq !== primeSeq) return null;
                if (!n || n.done || !n.value || !n.value.buffer) {
                    const ok = await advanceSegment();
                    if (!ok) return null;
                    continue;
                }
                return n.value.buffer;
            }
        };

        const writeProcessedBuffer = (buffer: AudioBuffer): number => {
            const timestamp = nextChunkTimeSec;
            const segRemainingSec = Math.max(0, seg.srcEndSec - nextChunkTimeSec);
            if (segRemainingSec <= EPS) return 0;

            let frames = buffer.length;
            let durationSec = frames / effectiveSampleRate;
            if (durationSec > segRemainingSec + EPS) {
                frames = Math.max(0, Math.floor(segRemainingSec * effectiveSampleRate));
                durationSec = frames / effectiveSampleRate;
                if (frames <= 0) return 0;
            }

            const channels = buffer.numberOfChannels;
            let planes: Float32Array[] = [];
            for (let ch = 0; ch < channels; ch++) {
                planes.push(buffer.getChannelData(ch).slice());
            }
            if (frames !== buffer.length) {
                planes = planes.map((p) => p.slice(0, frames));
            }

            // Apply fade envelope (clip edges + transition points)
            applyFadeEnvelope(
                planes,
                effectiveSampleRate,
                nextChunkTimeSec,
                durationSec,
                clipStartSrcSec,
                clipEndSrcSec,
                playbackRate,
                fadeIn,
                fadeOut,
                transitionPts
            );

            nextChunkTimeSec += durationSec;
            nextChunkCompTimeSec += durationSec;

            mixer.writePcm(this._mixerTrack!, { timestamp, frames, channels, planes });
            return durationSec;
        };

        // Prebuffer loop
        while (acc < prebufferSec) {
            if (this._audioPrimeSeq !== primeSeq) return;
            const buf = await readNextBuffer();
            if (!buf) break;
            const wroteSec = writeProcessedBuffer(buf);
            acc += wroteSec;
            if (seg.srcEndSec - nextChunkTimeSec <= EPS) {
                const ok = await advanceSegment();
                if (!ok) break;
            }
        }

        // Continuous feeder - capture configs for the async loop
        const capturedFadeIn = fadeIn;
        const capturedFadeOut = fadeOut;
        const capturedTransitionPts = transitionPts;
        const capturedStudioSound = this._studioSoundEnabled;
        const limiterState = this._limiterState;  // Reference to mutable state object
        const capturedClipStartSrcSec = clipStartSrcSec;
        const capturedClipEndSrcSec = clipEndSrcSec;
        const capturedPlaybackRate = playbackRate;

        (async () => {
            while (true) {
                if (this._audioPrimeSeq !== primeSeq) return;

                // Backpressure: don't decode far ahead of playhead (composition time).
                const comp = this.track?.composition as any;
                const playheadSec =
                    comp && typeof comp.getPlayheadTimeSec === "function"
                        ? Number(comp.getPlayheadTimeSec())
                        : Number(compTimeSec ?? 0);
                const bufferAhead = nextChunkCompTimeSec - playheadSec;
                if (Number.isFinite(bufferAhead) && bufferAhead > MAX_BUFFER_AHEAD_SEC) {
                    await sleep(BACKPRESSURE_SLEEP_MS);
                    continue;
                }

                const buf = await readNextBuffer();
                if (!buf) return;

                // Recompute planes each time (keep logic close to the old path).
                const timestamp = nextChunkTimeSec;
                const segRemainingSec = Math.max(0, seg.srcEndSec - nextChunkTimeSec);
                if (segRemainingSec <= EPS) {
                    const ok = await advanceSegment();
                    if (!ok) return;
                    continue;
                }

                const channels = buf.numberOfChannels;
                let frames = buf.length;
                let durationSec = frames / effectiveSampleRate;
                if (durationSec > segRemainingSec + EPS) {
                    frames = Math.max(0, Math.floor(segRemainingSec * effectiveSampleRate));
                    durationSec = frames / effectiveSampleRate;
                    if (frames <= 0) {
                        const ok = await advanceSegment();
                        if (!ok) return;
                        continue;
                    }
                }

                let planes: Float32Array[] = [];
                for (let ch = 0; ch < channels; ch++) {
                    planes.push(buf.getChannelData(ch).slice());
                }
                if (frames !== buf.length) {
                    planes = planes.map((p) => p.slice(0, frames));
                }

                // Apply fade envelope (clip edges + transition points)
                applyFadeEnvelope(
                    planes,
                    effectiveSampleRate,
                    nextChunkTimeSec,
                    durationSec,
                    capturedClipStartSrcSec,
                    capturedClipEndSrcSec,
                    capturedPlaybackRate,
                    capturedFadeIn,
                    capturedFadeOut,
                    capturedTransitionPts
                );

                nextChunkTimeSec += durationSec;
                nextChunkCompTimeSec += durationSec;

                if (this._audioPrimeSeq !== primeSeq) return;
                mixer.writePcm(this._mixerTrack!, { timestamp, frames, channels, planes });

                if (seg.srcEndSec - nextChunkTimeSec <= EPS) {
                    const ok = await advanceSegment();
                    if (!ok) return;
                }
            }
        })().catch(() => {});
    }

    public async prepareForPlayback(audioCtx: AudioContext, _masterGain: GainNode, mixer?: AudioMixer) {
        this._audioCtx = audioCtx;
        this._mixer = mixer;
        if (!this.source.input) return;

        if (this._mixer && !this._mixerTrack) {
            this._mixerTrack = this._mixer.registerTrack({
                clipId: this.id,
                trackId: this.track.id,
                channels: this._num_channels,
                sampleRate: this._sample_rate ?? audioCtx.sampleRate,
            });
            this._mixer.setClipGain(this._mixerTrack, this.gain);
        }
    }

    public stopAudio() {
        this._audioPrimeSeq += 1;
        void this._audioIter?.return?.();
        this._audioIter = undefined;
    }


    get volume(): number {
        return this.gain;
    }

    set volume(v: number) {
        this.gain = Math.max(0, v);
        if (this._mixer && this._mixerTrack) this._mixer.setClipGain(this._mixerTrack, this.gain);
    }

    public async connect(track: Track<AudioClip>): Promise<void> {
        super.connect(track);
    }

    public update(): void {
    }

    /**
     * Get whether Studio Sound (denoised audio) is enabled.
     */
    public get studioSoundEnabled(): boolean {
        return this._studioSoundEnabled;
    }

    /**
     * Set Studio Sound state and swap audio source accordingly.
     * @param enabled - Whether to use denoised audio
     * @param denoisedUrl - URL for denoised audio (required when enabling)
     * @param originalUrl - URL for original audio (required when disabling, or stored from first enable)
     */
    public async setStudioSound(enabled: boolean, denoisedUrl?: string, originalUrl?: string): Promise<void> {
        if (enabled === this._studioSoundEnabled) {
            return;
        }

        // Store URLs for future swapping
        if (originalUrl) this._originalSourceUrl = originalUrl;
        if (denoisedUrl) this._denoisedSourceUrl = denoisedUrl;

        const targetUrl = enabled ? this._denoisedSourceUrl : this._originalSourceUrl;

        if (!targetUrl) {
            console.warn('[AudioClip.setStudioSound] Missing URL for', enabled ? 'denoised' : 'original', 'audio');
            return;
        }

        await this.replaceSource(targetUrl);
        this._studioSoundEnabled = enabled;

        // Emit event for UI to update waveform
        this.trigger('studioSoundChanged', { enabled, clipId: this.id });
    }

    /**
     * Replace the audio source with a new URL.
     * Used for Studio Sound (denoised audio) switching.
     * @param newUrl - The new audio URL to load
     */
    public async replaceSource(newUrl: string): Promise<void> {
        // 1. Stop current audio playback
        this.stopAudio();

        // 2. Create new Input from URL
        const newInput = new Input({
            formats: ALL_FORMATS,
            source: new UrlSource(newUrl),
        });

        // 3. Update source reference (keep same ID for consistency)
        this.source = {
            ...this.source,
            input: newInput,
        };

        // 4. Reinitialize audio sink
        try {
            const audioTrack = await newInput.getPrimaryAudioTrack();
            if (!audioTrack) {
                throw new IOError({code: 'noAudioTrack', message: 'New source has no audio track.'});
            }
            if (audioTrack.codec === null || !(await audioTrack.canDecode())) {
                throw new IOError({code: 'unsupportedAudioCodec', message: 'Unsupported/undecodable audio codec.'});
            }

            this._audioSink = new AudioBufferSink(audioTrack);
            const newChannels = audioTrack.numberOfChannels;
            const newSampleRate = audioTrack.sampleRate;
            
            // Check if sample rate or channels changed - need to re-register mixer track
            const sampleRateChanged = this._sample_rate !== newSampleRate;
            const channelsChanged = this._num_channels !== newChannels;
            
            this._num_channels = newChannels;
            this._sample_rate = newSampleRate;

            // 5. Re-register mixer track if sample rate or channels changed
            // The mixer worklet uses the registered sample rate to convert time→sample index
            // Wrong rate = wrong samples = pitch shift (e.g., 44100Hz played at 48000Hz = helium voice)
            if (this._mixer && this._mixerTrack && (sampleRateChanged || channelsChanged)) {
                console.log('[AudioClip.replaceSource] Sample rate/channels changed, re-registering mixer track. Old:', 
                    this._sample_rate, '→ New:', newSampleRate);
                
                this._mixer.flushTrack(this._mixerTrack);
                
                // Re-register with correct sample rate
                this._mixerTrack = this._mixer.registerTrack({
                    clipId: this.id,
                    trackId: this.track?.id,
                    channels: newChannels,
                    sampleRate: newSampleRate,
                });
                this._mixer.setClipGain(this._mixerTrack, this.gain);
            } else if (this._mixer && this._mixerTrack) {
                // Same sample rate, just flush
                this._mixer.flushTrack(this._mixerTrack);
                this._mixer.setClipGain(this._mixerTrack, this.gain);
            }

            // 6. Emit event for UI to know source changed
            this.trigger('sourceChanged', { clipId: this.id });

            console.log('[AudioClip.replaceSource] Source replaced successfully, sampleRate:', newSampleRate);
        } catch (err) {
            console.error('[AudioClip.replaceSource] Failed to replace source:', err);
            throw err;
        }
    }

    /**
     * Create a deep clone of this audio clip with all properties.
     * The clone gets a new ID but shares the same source.
     */
    public clone(): AudioClip {
        const cloned = new AudioClip(this.source, {}, undefined);

        // Copy timing properties
        cloned._offset = Timestamp.fromSeconds(this._offset.seconds);
        cloned.duration.seconds = this.duration.seconds;
        cloned.range = [
            Timestamp.fromSeconds(this.range[0].seconds),
            Timestamp.fromSeconds(this.range[1].seconds)
        ];
        cloned._playbackRate = this._playbackRate;
        cloned.gain = this.gain;

        // Copy cuts (deep clone)
        cloned.cuts = this.cuts.map(c => ({
            id: crypto?.randomUUID?.() ?? `cut_${Date.now()}_${Math.random()}`,
            startSec: c.startSec,
            endSec: c.endSec,
            groupId: c.groupId
        }));
        
        // Copy fade configurations
        if (this.fadeIn) {
            cloned.fadeIn = { ...this.fadeIn };
        }
        if (this.fadeOut) {
            cloned.fadeOut = { ...this.fadeOut };
        }
        
        // Copy transition points (deep clone)
        cloned.transitionPoints = this.transitionPoints.map(tp => ({
            id: crypto?.randomUUID?.() ?? `tp_${Date.now()}_${Math.random()}`,
            srcTimeSec: tp.srcTimeSec,
            transition: tp.transition ? {
                fadeOut: tp.transition.fadeOut ? { ...tp.transition.fadeOut } : undefined,
                fadeIn: tp.transition.fadeIn ? { ...tp.transition.fadeIn } : undefined,
            } : undefined,
        }));

        // Copy name if set
        if (this.name) cloned.name = this.name;

        // Normalize and derive segments
        cloned.normalizeCuts();
        cloned.deriveSegments();

        return cloned;
    }

}
